/**
 * Colour ramps for the track. Nothing here talks to MapLibre — it just turns a
 * position `0..1` along a ramp into a hex colour, and hands the map layer a
 * lookup table (see `segments.ts`).
 *
 * Every ramp keeps a saturated colour at every stop, midpoint included, and
 * never passes through white, grey or black: the map's basemap tiles are
 * often near-white themselves, and a pale or neutral midpoint disappears
 * into them. Vertical speed is a diverging metric (descent/level/climb) and
 * altitude, speed and time are sequential ones, and a ramp shape that reads
 * both ways means the palette picker does not need to know which metric is
 * showing.
 */

export interface Palette {
  id: string;
  label: string;
  /** Hex stops, evenly spaced from 0 to 1. At least two. */
  stops: readonly string[];
}

const SPECTRUM: Palette = {
  id: 'spectrum',
  label: 'Spectrum',
  stops: ['#2b6cb0', '#3ba7a0', '#4caf50', '#f6ad55', '#c53030'],
};

export const PALETTES: readonly Palette[] = [
  SPECTRUM,
  {
    id: 'ocean',
    label: 'Ocean',
    stops: ['#0b3d5c', '#1c7d8c', '#2a9d8f', '#f4d35e', '#e07a1f'],
  },
  {
    id: 'contrast',
    label: 'Contrast',
    stops: ['#08519c', '#6a3d9a', '#e6198c', '#fd8d3c', '#d94801'],
  },
];

export const DEFAULT_PALETTE_ID = SPECTRUM.id;

export function getPalette(id: string): Palette {
  return PALETTES.find((palette) => palette.id === id) ?? SPECTRUM;
}

function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return [r, g, b];
}

function toHex(channel: number): string {
  return Math.round(channel).toString(16).padStart(2, '0');
}

/** `t` clamps to `[0, 1]`. Linear interpolation between the two nearest stops. */
export function sampleColour(palette: Palette, t: number): string {
  const clamped = Math.min(1, Math.max(0, t));
  const stops = palette.stops;
  const lastIndex = stops.length - 1;
  if (lastIndex <= 0) {
    return stops[0] ?? '#888888';
  }

  const scaled = clamped * lastIndex;
  const lowerIndex = Math.floor(scaled);
  const upperIndex = Math.min(lastIndex, lowerIndex + 1);
  const localT = scaled - lowerIndex;

  const lower = hexToRgb(stops[lowerIndex] ?? '#888888');
  const upper = hexToRgb(stops[upperIndex] ?? '#888888');
  const r = lower[0] + (upper[0] - lower[0]) * localT;
  const g = lower[1] + (upper[1] - lower[1]) * localT;
  const b = lower[2] + (upper[2] - lower[2]) * localT;
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** A fixed number of evenly spaced swatches, for a legend or a lookup table. */
export function bandColours(palette: Palette, bandCount: number): string[] {
  if (bandCount <= 1) {
    return [sampleColour(palette, 0.5)];
  }
  return Array.from({ length: bandCount }, (_, i) => sampleColour(palette, i / (bandCount - 1)));
}

/** Points with no reading for the active metric — always the same neutral grey. */
export const UNKNOWN_COLOUR = '#9ca3af';
